import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface SupplierCreateModalProps {
  context?: "finance" | "catalog";
  /** Prevents duplicate creation from the supplier directory while preserving invoice reuse flows. */
  preventExistingSupplier?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (supplier: { id: number; name: string; taxId?: string | null }) => void;
}

export function SupplierCreateModal({ open, onOpenChange, onCreated, context = "finance", preventExistingSupplier = false }: SupplierCreateModalProps) {
  const [form, setForm] = useState({
    name: "",
    taxId: "",
    contactName: "",
    contacts: [{ person: "", role: "", email: "", phone: "" }],
    notes: "",
    taxCountryCode: "ES",
  });
  const [consentPartnerId, setConsentPartnerId] = useState<number | null>(null);
  const identityQuery = trpc.financeSuppliers.inspectIdentity.useQuery(
    { taxId: form.taxId.trim(), taxCountryCode: form.taxCountryCode },
    { enabled: open && Boolean(form.taxId.trim()) && form.taxCountryCode.length === 2, retry: false },
  );
  const identity = identityQuery.data;
  const reuseRequired = Boolean(identity?.partnerId);
  const duplicateSupplier = preventExistingSupplier && identity?.kind === "existing_supplier";
  const cannotSave = !identity || identityQuery.isFetching || identity.kind === "review_required" || duplicateSupplier || (reuseRequired && consentPartnerId !== identity.partnerId);

  const mutationOptions = {
    onSuccess: (data: { id: number; name: string; taxId: string | null }) => {
      toast.success("Proveedor disponible para seleccionar");
      onCreated({ id: data.id, name: data.name, taxId: data.taxId });
      setForm({
        name: "",
        taxId: "",
        contactName: "",
        contacts: [{ person: "", role: "", email: "", phone: "" }],
        notes: "",
        taxCountryCode: "ES",
      });
      setConsentPartnerId(null);
      onOpenChange(false);
    },
    onError: (err: { message: string }) => {
      toast.error(`Error al crear proveedor: ${err.message}`);
      identityQuery.refetch();
    },
  };
  const financeMutation = trpc.financeSuppliers.create.useMutation(mutationOptions);
  const catalogMutation = trpc.productCatalog.createSupplier.useMutation(mutationOptions);
  const createMutation = context === "catalog" ? catalogMutation : financeMutation;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre del proveedor es obligatorio");
      return;
    }
    createMutation.mutate({
      name: form.name.trim(),
      contactName: form.contactName.trim() || undefined,
      contacts: form.contacts.map((contact) => ({
        person: contact.person.trim() || null,
        role: contact.role.trim() || null,
        email: contact.email.trim() || null,
        phone: contact.phone.trim() || null,
      })).filter((contact) => Object.values(contact).some(Boolean)),
      notes: form.notes.trim() || undefined,
      taxId: form.taxId.trim(),
      taxCountryCode: form.taxCountryCode,
      reusePartnerId: identity?.partnerId,
      consentReuse: reuseRequired && consentPartnerId === identity?.partnerId,
      rejectExistingSupplier: preventExistingSupplier,
    });
  }

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (field === "taxId" || field === "taxCountryCode") setConsentPartnerId(null);
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };
  }

  function updateContact(index: number, field: "person" | "role" | "email" | "phone", value: string) {
    setForm((previous) => ({ ...previous, contacts: previous.contacts.map((contact, current) => current === index ? { ...contact, [field]: value } : contact) }));
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crear proveedor</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              placeholder="Nombre de la empresa o proveedor"
              value={form.name}
              onChange={set("name")}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="taxId">CIF/NIF *</Label>
              <Input
                id="taxId"
                placeholder="Identidad fiscal del proveedor"
                value={form.taxId}
                onChange={set("taxId")}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contactName">Nombre comercial</Label>
              <Input
                id="contactName"
                placeholder="Opcional"
                value={form.contactName}
                onChange={set("contactName")}
              />
            </div>
          </div>

          <section className="space-y-3" aria-label="Contactos del proveedor">
            {form.contacts.map((contact, index) => (
              <div key={index} className="space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Contacto {index + 1}</p>
                  {index > 0 && <Button type="button" variant="ghost" size="sm" aria-label={`Eliminar contacto ${index + 1}`} onClick={() => setForm((previous) => ({ ...previous, contacts: previous.contacts.filter((_, current) => current !== index) }))}><Trash2 className="mr-1 h-4 w-4" />Eliminar</Button>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor={`contactPerson-${index}`}>Persona de contacto</Label><Input id={`contactPerson-${index}`} placeholder="Nombre y apellidos" value={contact.person} onChange={(event) => updateContact(index, "person", event.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor={`contactRole-${index}`}>Cargo o departamento</Label><Input id={`contactRole-${index}`} placeholder="Comercial, facturación…" value={contact.role} onChange={(event) => updateContact(index, "role", event.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor={`contactEmail-${index}`}>Email</Label><Input id={`contactEmail-${index}`} type="email" placeholder="Opcional" value={contact.email} onChange={(event) => updateContact(index, "email", event.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor={`contactPhone-${index}`}>Teléfono</Label><Input id={`contactPhone-${index}`} type="tel" placeholder="Opcional" value={contact.phone} onChange={(event) => updateContact(index, "phone", event.target.value)} /></div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm((previous) => ({ ...previous, contacts: [...previous.contacts, { person: "", role: "", email: "", phone: "" }] }))}><Plus className="mr-1.5 h-4 w-4" />Añadir otro contacto</Button>
          </section>

          <div className="space-y-1.5">
            <Label htmlFor="supplierCountry">País fiscal (código de dos letras)</Label>
            <Input id="supplierCountry" maxLength={2} value={form.taxCountryCode} onChange={(event) => {
              setConsentPartnerId(null);
              setForm((previous) => ({ ...previous, taxCountryCode: event.target.value.toUpperCase() }));
            }} />
          </div>
          {identityQuery.isFetching && <p role="status" className="text-sm">Comprobando identidad fiscal…</p>}
          {identityQuery.isError && <p role="alert" className="text-sm text-destructive">No se pudo comprobar la identidad. {identityQuery.error.message}</p>}
          {identity?.kind === "review_required" && <p role="alert" className="rounded-md border p-3 text-sm text-destructive">{identity.reason}</p>}
          {identity && identity.kind !== "new" && identity.kind !== "review_required" && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
              <p>Ya existe: <strong>{identity.name}</strong>{identity.isCustomer ? " (también es cliente)" : ""}. Se conservará su identidad y el ID operativo del proveedor.</p>
              {reuseRequired ? <label className="flex items-start gap-2">
                <input type="checkbox" checked={consentPartnerId === identity.partnerId} onChange={(event) => setConsentPartnerId(event.target.checked ? identity.partnerId! : null)} />
                <span>Confirmo reutilizar esta entidad y activar únicamente su rol de proveedor. No se modificará su perfil de cliente.</span>
              </label> : <p>Se reutilizará el proveedor existente; no se creará un duplicado.</p>}
              <p className="text-xs text-muted-foreground">Al reutilizar una entidad se conservan sus datos fiscales y de contacto. Los campos nuevos no los sobrescriben.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Observaciones opcionales..."
              value={form.notes}
              onChange={set("notes")}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || cannotSave || !form.name.trim()}>
              {createMutation.isPending ? "Guardando..." : identity?.kind === "new" ? "Crear proveedor" : "Reutilizar proveedor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <Dialog open={Boolean(open && duplicateSupplier)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Este CIF/NIF ya está dado de alta</DialogTitle>
          <DialogDescription>
            El CIF/NIF <strong>{form.taxId.trim()}</strong> ya corresponde al proveedor <strong>{identity?.name}</strong>.
            Revisa los datos del proveedor existente antes de continuar. No se creará un duplicado.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

